import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { css } from 'styled-components'
import dsTheme from '../constants/design-system/theme.json'

import { theme } from '../constants/theme'

import withApp from '../components/withApp/withApp'
import SEO from '../components/Seo'
import TrustPilot from '../components/TrustPilot'
import PlanSection from '../components/PlanSection'
import TextLink from '../components/TextLink'
import Button from '../components/Button'

// Modular system components
import Hero from '../modular-system/Hero'
import FaqSection from '../modular-system/FaqSection'
import FiftyFifty from '../modular-system/FiftyFifty'
import LogoGarden from '../modular-system/LogoGarden'

// Global Styled Components
import CenteredButton from '../components/styles/CenteredButton'
import FullWidthContainer from '../components/styles/FullWidthContainer'
import SiteContainer from '../components/styles/SiteContainer'
import { Header2 } from '../components/styles/FontSize'
import Spacer from '../components/styles/Spacer'

import HeroImage from '../static/images/seo-landing-pages/no-credit-check-people.svg'
import DataSimDevice from '../static/images/seo-landing-pages/data-sim-device.svg'
import PayAsYouGoMobile from '../static/images/seo-landing-pages/pay-as-you-go-mobile.svg'
import SimOnItsWay from '../static/images/seo-landing-pages/sim-on-its-way.svg'
import UnlimitedImage from '../static/images/seo-landing-pages/no-credit-check-unlimited.svg'
import { RICH_DATA_STRINGS } from '../constants/globalStrings'

const { colors } = dsTheme

const FreeSim = ({ plans: { plans } }) => (
  <Fragment>
    <SEO
      itemType={RICH_DATA_STRINGS.FAQ_ITEM_TYPE}
      title={freeSimContent.seo.title}
      description={freeSimContent.seo.description}
      keywords={freeSimContent.seo.keywords}
    />

    <FullWidthContainer background={colors['UI/DarkGrey']}>
      <SiteContainer>
        <Hero
          heading={freeSimContent.heading}
          subHeading={freeSimContent.subHeading}
          subTitle={freeSimContent.subTitle}
          benefits={freeSimContent.benefits}
          CTA={freeSimContent.heroCta}
          imageUrl={HeroImage}
          imageAlt={freeSimContent.heroImageAlt}
          CTALink={freeSimContent.CTALink}
        />
      </SiteContainer>
    </FullWidthContainer>

    <SiteContainer>
      <LogoGarden
        css={css`
          margin-left: 0;
          margin-right: 0;
        `}
      />
    </SiteContainer>

    <SiteContainer
      data-module-id={plans.length > 0 ? 'plan-modules-data-sim' : null}
      data-module_name="simple-plans"
      data-module_placement="products-section"
    >
      <PlanSection plans={plans} hideDataDiscountPlans />
    </SiteContainer>

    <SiteContainer
      style={{
        backgroundColor: colors['UI/DarkGrey'],
      }}
    >
      <FiftyFifty imageTextSmall inverse>
        <img src={freeSimContent.heroImgSrc} alt="" />
        {freeSimContent.heroContent}
      </FiftyFifty>
    </SiteContainer>

    <SiteContainer>
      {freeSimContent.imageTextSections.slice(0).map((fiftyFifty, index) => (
        <Fragment>
          <FiftyFifty
            key={index}
            index={index}
            imageTextSmall
            inverse={Math.abs(index % 2) === 1}
          >
            <img src={fiftyFifty.imageSrc} alt="" />
            {fiftyFifty.content}
          </FiftyFifty>
        </Fragment>
      ))}
    </SiteContainer>

    <FullWidthContainer background={colors['UI/DarkGrey']}>
      <SiteContainer>
        <TrustPilot
          dataTags="SelectedReview"
          dataTestId="homepage-trustpilot-widget"
        />
      </SiteContainer>
    </FullWidthContainer>

    <SiteContainer>
      <Spacer />
      <FaqSection faqAccordions={freeSimContent.faqAccordions} />
      <Spacer />
    </SiteContainer>

    <SiteContainer>
      <CenteredButton>
        <Button
          href={freeSimContent.CTALink}
          color={theme.colors.button.buttonColor}
        >
          {freeSimContent.heroCta}
        </Button>
      </CenteredButton>
    </SiteContainer>
    <Spacer />
  </Fragment>
)

const freeSimContent = {
  heading: 'Order your free SIM from SMARTY',
  subHeading:
    'Enjoy the flexibility of our free SIM deals, just pay for your usage.',
  subTitle: '',
  benefits: [
    'No long contracts',
    'Data plans from just 1GB',
    'No credit check',
    'Unlimited calls & texts',
  ],
  heroContent: (
    <Fragment>
      <Header2>
        The benefits of pay as you go. The convenience of a contract.
      </Header2>
      <p className="margin--regular kollektif-small">
        When you order a free SIM from SMARTY, you can pick a plan that’s right
        for you and set it to automatically renew – so you enjoy all the
        convenience of a pay monthly contract.
      </p>
      <p className="margin--regular kollektif-small">
        {' '}
        But, with <TextLink to="/no-credit-check-sim">no credit check</TextLink>
        , no contract (so you can cancel at any time), and the flexibility to
        add more data when you need it, you also get all the benefits of a{' '}
        <TextLink to="/pay-as-you-go">pay as you go</TextLink> SIM.
      </p>
      <p className="margin--regular kollektif-small">
        If you have a SIM-free phone, our free SIM card is the perfect fit.
      </p>
    </Fragment>
  ),
  heroImgSrc: PayAsYouGoMobile,
  heroImageAlt: 'Pay as you go mobile',
  heroCta: 'View our plans',
  CTALink: '#plans',
  seo: {
    itemType: 'https://schema.org/FAQPage',
    title: 'Order Your Free SIM Card Now',
    description:
      'Order your Free Pay As You Go SIM. SMARTY is a new SIM-only mobile network that’s built to be fair, transparent and smart.',
    keywords: ['Free SIM', 'Free SIM deals', 'Free SIM', 'Free SIM'],
  },
  faqAccordions: [
    {
      faqId: 'free-sim-card',
      faqTitle: 'What is a free SIM card?',
      faqTitleTestId: 'free-sim-card-title',
      faqContent:
        'All our SMARTY SIM cards are free to order. The only thing you have to pay for is the data plan you want. This is paid in advance to avoid any unexpected bills from going over your usage allowance. To use our free SIM cards you’ll need an unlocked SIM free phone. Speak with your current network provider if you need your phone unlocked.',
      faqContentTestId: 'free-sim-card-content',
    },
    {
      faqId: 'get-free-sim-card',
      faqTitle: 'Where can I get a free SIM card from?',
      faqTitleTestId: 'get-free-sim-card-title',
      faqContent:
        'You can order a free SIM card online from our website or via the SMARTY app. Alternatively, you can pick up a SIM card from larger supermarkets or independent corner shops, but there may be a charge.',
      faqContentTestId: 'free-sim-card-content',
    },
    {
      faqId: 'free-sim-card-instore',
      faqTitle: 'Can I pick up a free SIM card in store?',
      faqTitleTestId: 'free-sim-card-instore-title',
      faqContent:
        'Although SMARTY is primarily an online provider, you can pick up our SIM cards in large supermarkets and independent corner shops. However, you may have to pay around £1, if you pick one up in-store, but they are completely free if you order them from our website.',
      faqContentTestId: 'free-sim-card-instore-content',
    },
    {
      faqId: 'activate-free-sim-card',
      faqTitle: 'How do I activate a free SIM card?',
      faqTitleTestId: 'activate-free-card-title',
      faqContent:
        'Already got your free SIM? Log in to your account and tap “Activate SIM” to begin your billing month and start using your data, calls & texts. Activation should happen in just a few minutes, but could take up to 2 hours. We’ll send you an email and a text to your SMARTY SIM when your SIM is activated. If you want to keep your old number, you can request a PAC code from your current provider.',
      faqContentTestId: 'activate-free-sim-card-content',
    },
  ],
  imageTextSections: [
    {
      imageSrc: SimOnItsWay,
      content: (
        <Fragment>
          <Header2>The best free SIM card for your SIM-free phone</Header2>
          <p className="margin--regular kollektif-small">
            If you’ve got an unlocked SIM-free phone, just pop in one of our
            free SIM cards and you’ll be connected in no time. Simply follow the
            instructions on the pack we send you.
          </p>
        </Fragment>
      ),
    },
    {
      imageSrc: DataSimDevice,
      content: (
        <Fragment>
          <Header2>A great free SIM for your tablet, dongle and more</Header2>
          <p className="margin--regular kollektif-small">
            Our free SIMs also work with tablets and mobile broadband devices
            like dongles and WiFi routers. So all you need to do is choose your
            plan, pop in your free SIM card, and you’re ready to go{' '}
          </p>
          <p className="margin--regular kollektif-small">
            Just another reason why we think ours are the best free SIM cards in
            the UK.
          </p>
        </Fragment>
      ),
    },
    {
      imageSrc: UnlimitedImage,
      content: (
        <Fragment>
          <Header2>Unlimited UK standard texts and calls</Header2>
          <p className="margin--none">
            Once you’ve activated a plan on our free SIM card, UK standard calls
            and texts are completely unlimited. So there’ll be no more excuses
            for not catching up with friends and family.
          </p>
        </Fragment>
      ),
    },
  ],
}

FreeSim.propTypes = {
  plans: {
    plans: PropTypes.array.isRequired,
  },
}

export default withApp(FreeSim)
